import * as Sentry from '@sentry/nuxt'
import { browserTracingIntegration, replayIntegration, vueIntegration } from '@sentry/nuxt'

import {
  COMMON_IGNORE_ERRORS,
  DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE,
  DEFAULT_REPLAYS_SESSION_SAMPLE_RATE,
  DEFAULT_TRACE_PROPAGATION_TARGETS,
  DEFAULT_TRACES_SAMPLE_RATE,
  isProductionLike,
} from './shared'

type ClientInitOptions = NonNullable<Parameters<typeof Sentry.init>[0]>
type SentryIntegration = ReturnType<typeof browserTracingIntegration>
type BrowserTracingOptions = NonNullable<Parameters<typeof browserTracingIntegration>[0]>

export type SentryClientOptions = Omit<ClientInitOptions, 'integrations'> & {
  /** Resolved deploy environment (e.g. from runtimeConfig.public.environment). */
  environment: string
  /** Opt in to session replay (off by default — carries quota/privacy cost). */
  replay?: boolean
  /** Optional Vue Router for precise route-span names. */
  router?: BrowserTracingOptions['router']
  /** Extra integrations, appended to the shared defaults. */
  integrations?: SentryIntegration[]

  debug?: boolean
}

const noiseFilteringBeforeSend: ClientInitOptions['beforeSend'] = (event) => {
  if (typeof event.message === 'string' && event.message.startsWith('ReportingObserver')) {
    return null
  }

  const body = event.extra?.body
  const sourceFile = body && typeof body === 'object' && 'sourceFile' in body ? (body as { sourceFile?: string }).sourceFile : undefined

  if (sourceFile && (sourceFile.startsWith('chrome-extension') || sourceFile.startsWith('https://app.usercentrics.eu'))) {
    return null
  }

  return event
}

export const initSentryClient = ({
  environment,
  enabled,
  replay = false,
  router,
  integrations = [],
  ignoreErrors = [],
  debug = false,
  ...rest
}: SentryClientOptions): void => {
  Sentry.init({
    environment,
    enabled: enabled ?? isProductionLike(environment),
    debug,
    tracesSampleRate: DEFAULT_TRACES_SAMPLE_RATE,
    tracePropagationTargets: [...DEFAULT_TRACE_PROPAGATION_TARGETS],
    beforeSend: noiseFilteringBeforeSend,
    ...(replay
      ? {
          replaysSessionSampleRate: DEFAULT_REPLAYS_SESSION_SAMPLE_RATE,
          replaysOnErrorSampleRate: DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE,
        }
      : {}),
    ...rest,
    integrations: [
      browserTracingIntegration(router ? { router } : {}),
      vueIntegration(),
      ...(replay ? [replayIntegration()] : []),
      ...integrations,
    ],
    ignoreErrors: [...COMMON_IGNORE_ERRORS, ...ignoreErrors],
  })
}
